import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Chip from '@mui/joy/Chip';
import Tooltip from '@mui/joy/Tooltip';
import DashboardTable from '../../components/DashboardTable';
import DashboardPageHeader from '../../components/DashboardPageHeader';
import PageStack from '../../components/PageStack';
import { LAYOUT } from '../../theme/layoutTokens';
import TableCustomerCell from '../../components/TableCustomerCell';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import Alert from '@mui/joy/Alert';
import CircularProgress from '@mui/joy/CircularProgress';
import Stack from '@mui/joy/Stack';
import { clientsApi } from '../../api/clients.api';
import { Client, ClientType, CLIENT_TYPE_LABELS } from '../../types/client';
import { debounce } from 'lodash';

type ClientTypeFilter = 'all' | ClientType;

const TYPE_CHIP_COLOR: Record<ClientType, 'primary' | 'warning' | 'success'> = {
  [ClientType.CLIENT]: 'primary',
  [ClientType.SUPPLIER]: 'warning',
  [ClientType.PERSONAL]: 'success',
};

const ClientList: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ClientTypeFilter>('all');

  const isSearchMode = appliedSearch.trim().length > 0;

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (isSearchMode) {
        const results = await clientsApi.search(appliedSearch.trim());
        setClients(results);
        setTotalItems(results.length);
        setTotalPages(1);
      } else {
        const activeType = typeFilter !== 'all' ? typeFilter : undefined;
        const response = await clientsApi.getAll(currentPage, itemsPerPage, activeType);
        if (response && response.data) {
          setClients(response.data);
          setTotalItems(response.total || 0);
          setTotalPages(response.lastPage || 1);
        } else {
          setClients([]);
          setError('Error al cargar los datos de contactos');
        }
      }
    } catch {
      setClients([]);
      setError('Error al cargar los datos de contactos');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, isSearchMode, appliedSearch, typeFilter]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const debouncedApplySearch = useMemo(
    () =>
      debounce((value: string) => {
        setAppliedSearch(value);
        setCurrentPage(1);
      }, 300),
    [],
  );

  useEffect(() => () => debouncedApplySearch.cancel(), [debouncedApplySearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedApplySearch(value);
  };

  const handleDelete = async (reference: string) => {
    if (window.confirm('¿Estás seguro de eliminar este contacto?')) {
      try {
        await clientsApi.delete(reference);
        loadClients();
      } catch {
        setError('Error al eliminar el contacto');
      }
    }
  };

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const isFilterActive = typeFilter !== 'all';

  if (loading && clients.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: LAYOUT.formMaxWidth, mx: 'auto', width: '100%' }}>
        <Alert color="danger" endDecorator={
          <Button size="sm" color="danger" variant="solid" onClick={loadClients}>
            Reintentar
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <PageStack spacing={LAYOUT.stackSurfaceGap}>
      <DashboardPageHeader
        title="Contactos"
        action={
          <Button component={RouterLink} to="/clients/new" color="primary">
            Nuevo contacto
          </Button>
        }
      />

      <Sheet
        variant="outlined"
        sx={{
          borderRadius: 'md',
          boxShadow: 'sm',
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
        }}
      >
        <Input
          size="sm"
          startDecorator={<SearchRoundedIcon />}
          placeholder="Buscar por nombre o teléfono..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <Select<ClientTypeFilter>
          size="sm"
          value={typeFilter}
          onChange={(_, value) => {
            setTypeFilter(value ?? 'all');
            setCurrentPage(1);
          }}
          sx={{ minWidth: 180 }}
        >
          <Option value="all">Todos los tipos</Option>
          <Option value={ClientType.CLIENT}>{CLIENT_TYPE_LABELS[ClientType.CLIENT]}</Option>
          <Option value={ClientType.SUPPLIER}>{CLIENT_TYPE_LABELS[ClientType.SUPPLIER]}</Option>
          <Option value={ClientType.PERSONAL}>{CLIENT_TYPE_LABELS[ClientType.PERSONAL]}</Option>
        </Select>
        {(searchTerm || isFilterActive) && (
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            onClick={() => {
              setSearchTerm('');
              setAppliedSearch('');
              setTypeFilter('all');
              setCurrentPage(1);
            }}
          >
            Limpiar
          </Button>
        )}
      </Sheet>

      <Sheet variant="outlined" sx={{ overflow: 'auto', borderRadius: 'md', boxShadow: 'sm', p: 0 }}>
        <DashboardTable stickyHeader>
          <thead>
            <tr>
              <th style={{ width: '26%' }}>Nombre</th>
              <th style={{ width: '20%' }}>Teléfono</th>
              <th style={{ width: '14%' }}>Tipo</th>
              <th style={{ width: '18%' }}>Fecha de creación</th>
              <th style={{ width: '22%', textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {clients && clients.length > 0 ? (
              clients.map((client) => (
                <tr key={client.reference}>
                  <td>
                    <TableCustomerCell name={client.name} />
                  </td>
                  <td>{client.phoneNumber}</td>
                  <td>
                    {client.clientType ? (
                      <Chip
                        size="sm"
                        variant="soft"
                        color={TYPE_CHIP_COLOR[client.clientType]}
                      >
                        {CLIENT_TYPE_LABELS[client.clientType]}
                      </Chip>
                    ) : (
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        —
                      </Typography>
                    )}
                  </td>
                  <td>{new Date(client.createdAt).toLocaleDateString()}</td>
                  <td>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Ver detalle" variant="soft" size="sm">
                        <IconButton
                          component={RouterLink}
                          to={`/clients/${client.reference}`}
                          size="sm"
                          variant="plain"
                          color="neutral"
                          aria-label="Ver detalle"
                        >
                          <VisibilityRoundedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar contacto" variant="soft" size="sm">
                        <IconButton
                          component={RouterLink}
                          to={`/clients/${client.reference}/edit`}
                          size="sm"
                          variant="plain"
                          color="primary"
                          aria-label="Editar"
                        >
                          <EditRoundedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar contacto" variant="soft" size="sm">
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleDelete(client.reference)}
                          aria-label="Eliminar"
                        >
                          <DeleteRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>
                  <Typography level="body-sm" sx={{ py: 2, textAlign: 'center', color: 'neutral.500' }}>
                    {appliedSearch || isFilterActive
                      ? 'No se encontraron contactos con esos filtros'
                      : 'No hay contactos para mostrar'}
                  </Typography>
                </td>
              </tr>
            )}
          </tbody>
        </DashboardTable>

        {!isSearchMode && totalPages > 0 && (
          <Box
            sx={{
              ...LAYOUT.tableFooterPadding,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              gap: LAYOUT.stackDense,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography level="body-sm">
              Mostrando{' '}
              {clients.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} a{' '}
              {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} contactos
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                disabled={currentPage === 1}
                onClick={() => paginate(currentPage - 1)}
                startDecorator={<ChevronLeftRoundedIcon />}
              >
                Anterior
              </Button>
              {[...Array(totalPages)].map((_, i) => (
                <Button
                  key={i}
                  size="sm"
                  variant={currentPage === i + 1 ? 'solid' : 'outlined'}
                  color={currentPage === i + 1 ? 'primary' : 'neutral'}
                  onClick={() => paginate(i + 1)}
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                disabled={currentPage === totalPages}
                onClick={() => paginate(currentPage + 1)}
                endDecorator={<ChevronRightRoundedIcon />}
              >
                Siguiente
              </Button>
            </Stack>
          </Box>
        )}
      </Sheet>
    </PageStack>
  );
};

export default ClientList;
